import { PATH_LOGIN } from "@configs";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import {
    Children,
    PropsWithChildren,
    ReactElement,
    cloneElement,
    useEffect,
    useRef,
    useState,
} from "react";
import { useLocation } from "react-router";
import styled from "styled-components";

type LoadOverlayType = {
    active: boolean;
    radius?: number;
    text?: string;
    blur?: number;
};

/**
 * A wrapper for loading component preventing interactivity. It wraps around content size.
 * ```jsx
 * // Example
 * import LoadOverlay from "./shared/LoadOverlay";
 *
 *     <LoadOverlay active={isActive}>
 *       <MyContents>
 *     </LoadOverlay>
 *   )
 * }
 * ```
 */
export default function LoadOverlay(props: PropsWithChildren<LoadOverlayType>) {
    const exludePaths = [PATH_LOGIN];

    const { pathname } = useLocation();
    if (exludePaths.includes(pathname)) return <></>;

    const [biggestChildSize, setBiggestChildSize] = useState({ width: 0, height: 0 });
    const [biggestChildIndex, setBiggestChildIndex] = useState<number | null>(null);
    const childrenRefs: Array<React.RefObject<HTMLDivElement>> = [];

    useEffect(() => {
        let maxArea = 0;
        let maxChildSize = { width: 0, height: 0 };
        let maxChildIndex: number | null = null;

        Children.forEach(props.children, (child: any, index: number) => {
            if (child && child.type === "div") {
                const rect = childrenRefs[index].current?.getBoundingClientRect();
                if (rect) {
                    const area = rect.width * rect.height;
                    if (area > maxArea) {
                        maxArea = area;
                        maxChildSize = { width: rect.width, height: rect.height };
                        maxChildIndex = index;
                    }
                }
            }
        });

        setBiggestChildSize(maxChildSize);
        setBiggestChildIndex(maxChildIndex);
    }, [props.children]);

    return (
        <Container>
            {Children.map(props.children, (child: any, index: number) => {
                childrenRefs[index] = useRef<HTMLDivElement>(null);

                return cloneElement(child as ReactElement<any>, {
                    ref: childrenRefs[index],
                });
            })}
            {biggestChildIndex !== null && props.active && (
                <LoadingOverlay
                    active={props.active}
                    width={biggestChildSize.width}
                    height={biggestChildSize.height}
                    top={childrenRefs[biggestChildIndex].current?.offsetTop ?? 0}
                    left={childrenRefs[biggestChildIndex].current?.offsetLeft ?? 0}
                    radius={props?.radius}
                    blur={props?.blur}
                >
                    <LazyMotion features={domAnimation}>
                        <AnimatePresence>
                            <Capsule
                                animate={{ rotate: [0, 360] }}
                                transition={{
                                    type: "tween",
                                    easings: ["linear"],
                                    repeat: Infinity,
                                    repeatType: "loop",
                                    duration: 1,
                                }}
                            >
                                <Circle
                                    animate={{ height: [50, 5], width: [30, 5] }}
                                    transition={{
                                        type: "tween",
                                        easings: ["linear"],
                                        repeat: Infinity,
                                        repeatType: "reverse",
                                        duration: 1,
                                    }}
                                />
                            </Capsule>
                        </AnimatePresence>
                    </LazyMotion>
                    {props.text && <LoadingText>{props.text}</LoadingText>}
                </LoadingOverlay>
            )}
        </Container>
    );
}

//#region styles

const Container = styled.div``;
const LoadingOverlay = styled.div<{
    active: boolean;
    width: number;
    height: number;
    top: number;
    left: number;
    radius?: number;
    blur?: number;
}>`
    position: absolute;
    top: ${({ top }) => top}px;
    left: ${({ left }) => left}px;

    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;

    border-radius: ${({ radius }) => radius}px;
    background-color: rgba(0, 0, 0, 0.5);

    backdrop-filter: blur(${({ blur }) => blur}px);
    -webkit-backdrop-filter: blur(${({ blur }) => blur}px);

    z-index: 9999;

    pointer-events: ${(props) => (props.active ? "all" : "none")};

    display: flex;
    justify-content: center;
    align-items: center;
`;
const Capsule = styled(m.div)`
    position: absolute;
    width: 30px;
    height: 50px;
    border-radius: 25px;
    border-top: solid 5px white;
    border-left: solid 5px white;
    border-right: solid 5px white;
    border-bottom: solid 5px white;

    display: flex;
    justify-content: center;
    align-items: center;
`;
const Circle = styled(m.div)`
    position: absolute;
    width: 5px;

    background-color: white;
    border-radius: 25px;
`;
const LoadingText = styled.div`
    color: white;
    padding-top: 80px;
    font-weight: 600;
`;

//#endregion
